import { Fragment, memo, useRef } from 'react';
import { areEqual } from 'react-window';
import { memoize } from 'lodash';
import { useTheme } from 'styled-components';
import { Form } from 'antd5';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  LoadingOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { ErrorAlert, WarningAlert } from 'components/ui';

import {
  ErrorWrapper,
  FilesSelected,
  SelectedFile,
  StyledList,
  StyledFilename,
  StyledRemoveButton,
  StyledFileNameRow,
} from './SelectedFileList.style';
import { FileUploadProgressSummary } from '../FileUploadProgressSummary';
import { DRAG_AND_DROP_DIRECTORY_ERROR, useUploadContext } from 'components/FileUploader';
import { FilteredFiles, UploadFile } from '../../types';
import { AtlasGqlAttachmentRecordType } from 'types/atlas-graphql';
import { EditAttachmentForm } from 'components/RecordAttachments/EditAttachmentModal/EditAttachmentForm';

const DEFAULT_ITEM_SIZE = 35;
const TALL_ITEM_SIZE = 200;

interface Data {
  fileName: string;
  listRef: React.Ref<any>;
  onRemove: (name: string) => void;
  selectedFiles: UploadFile[];
  setSelectedFiles: any;
  uploading?: boolean;
  validFilenames: string[];
  displayAttachmentOptions?: boolean;
  attachmentRecordParentType?: AtlasGqlAttachmentRecordType;
}

interface SelectedFileItemProps {
  data: Data;
  index: number;
  style?: React.CSSProperties;
}

const SelectedFileItem = memo(({ data, index, style }: SelectedFileItemProps) => {
  const theme = useTheme();

  const {
    validFilenames,
    selectedFiles,
    setSelectedFiles,
    uploading,
    onRemove,
    listRef,
    displayAttachmentOptions = false,
    attachmentRecordParentType = AtlasGqlAttachmentRecordType.Other,
  } = data;
  const file = selectedFiles[index];
  const { status } = file;

  const loadingIcon = <LoadingOutlined style={{ color: theme.teal }} spin />;
  const completeIcon = <CheckCircleOutlined style={{ color: theme.green }} />;
  const warningIcon = <WarningOutlined style={{ color: theme.red }} />;
  const pendingIcon = <ClockCircleOutlined />;

  // Callback to handle mapping the form values back to the files
  // so that they can be passed with the file data to the mutation call.
  const handleAttachmentValuesChange = () => {
    const values = form.getFieldsValue();
    if (setSelectedFiles) {
      const tempSelectedFiles = [...selectedFiles];
      tempSelectedFiles[index] = { ...file, ...values };
      setSelectedFiles(tempSelectedFiles);
    }
  };

  // Instantiate the form
  const [form] = Form.useForm();

  return (
    <SelectedFile key={file.sourceName + index} style={style}>
      <StyledFileNameRow>
        <StyledFilename invalid={!validFilenames.includes(file.sourceName)}>
          {<b>{file.sourceName}</b>}
        </StyledFilename>
        {uploading ? (
          status === 'error' ? (
            warningIcon
          ) : status === 'completed' ? (
            completeIcon
          ) : status === 'uploading' ? (
            loadingIcon
          ) : (
            pendingIcon
          )
        ) : (
          <StyledRemoveButton
            type="link"
            danger={true}
            value={file.sourceName}
            icon={<CloseOutlined />}
            onClick={e => {
              onRemove((e.currentTarget as HTMLButtonElement).value);
              (listRef as any)?.current?.resetAfterIndex(index, false);
            }}
          />
        )}
      </StyledFileNameRow>
      {displayAttachmentOptions && (
        <EditAttachmentForm
          form={form}
          handleChange={handleAttachmentValuesChange}
          categoryOnChange={handleAttachmentValuesChange}
          parentType={attachmentRecordParentType}
          disabled={uploading || !file.valid}
          /**
           * Pull default values from selectedFiles data. Initially these will be
           * undefined, but this prevents fields from appearing empty after
           * scrolling around the virtualized list and unmounting this component.
           */
          defaultCategoryId={selectedFiles[index].category}
          defaultDescription={selectedFiles[index].description}
        />
      )}
    </SelectedFile>
  );
}, areEqual);

// Memoized wrapper around the set of data that each list item will need
const createItemData = memoize(
  (
    validFilenames,
    selectedFiles,
    setSelectedFiles,
    uploading,
    fileName,
    onRemove,
    listRef,
    displayAttachmentOptions,
    attachmentRecordParentType
  ) => ({
    validFilenames,
    selectedFiles,
    setSelectedFiles,
    uploading,
    fileName,
    onRemove,
    listRef,
    displayAttachmentOptions,
    attachmentRecordParentType,
  })
);

export interface SelectedFileListProps {
  errorMessage?: React.ReactNode;
  fileName: string;
  filteredFiles: FilteredFiles;
  corruptFiles: string[];
  onRemove: (name: string) => void;
  showErrorSummary?: boolean;
  uploading?: boolean;
  validFiles: UploadFile[];
  displayAttachmentOptions?: boolean;
  attachmentRecordParentType?: AtlasGqlAttachmentRecordType;
  isBulkUpload?: boolean;
  showDragAndDropDirectoryError?: boolean;
  height?: number;
  // Set width to null if the list should take up the full space provided.
  width?: number | null;
}

export function SelectedFileList({
  errorMessage,
  fileName,
  filteredFiles,
  corruptFiles = [],
  onRemove,
  showErrorSummary,
  uploading,
  validFiles,
  displayAttachmentOptions = false,
  attachmentRecordParentType,
  isBulkUpload,
  showDragAndDropDirectoryError,
  height = 330,
  width = 496,
}: SelectedFileListProps) {
  const { selectedFiles, setSelectedFiles } = useUploadContext();
  const validFilesCount = validFiles.length;
  const validFilenames = validFiles.map(file => file.sourceName);
  const invalidCount = selectedFiles.length - validFilesCount;

  function itemKey(index: number, data: { selectedFiles: UploadFile[] }) {
    // Find the item at the specified index.
    // In this case "data" is an Array that was passed to List as "itemData".
    const itemKey = data?.selectedFiles[index].sourceName;

    // Return a value that uniquely identifies this item.
    // Typically this will be a UID of some sort.
    return itemKey;
  }

  const listRef = useRef<any>();

  const itemData = createItemData(
    validFilenames,
    selectedFiles,
    setSelectedFiles,
    uploading,
    fileName,
    onRemove,
    listRef,
    displayAttachmentOptions,
    attachmentRecordParentType
  );

  const finalInvalidCount =
    corruptFiles.length === 0 ? invalidCount : invalidCount - corruptFiles.length;

  return (
    <Fragment>
      {showErrorSummary ? (
        <ErrorSummary validFilesCount={validFilesCount} />
      ) : (
        <Fragment>
          {errorMessage && <ErrorAlert message={errorMessage} />}

          {uploading ? (
            <FileUploadProgressSummary />
          ) : (
            <Fragment>
              {corruptFiles.length > 0 && (
                <WarningAlert
                  message={`${corruptFiles.length} files will not be uploaded because they are corrupt.`}
                />
              )}
              {finalInvalidCount > 0 && (
                <WarningAlert
                  message={`${finalInvalidCount} files will not be uploaded because they are invalid.`}
                />
              )}
              {isBulkUpload && validFilesCount == 0 && (
                <WarningAlert
                  message={`No valid files found. Images will be downloaded from URL's if defined in the provided details file.`}
                />
              )}
              {filteredFiles.status && (
                <WarningAlert
                  message={`${filteredFiles.count} .DS_Store files have been filtered out because they are not necessary`}
                />
              )}
              {showDragAndDropDirectoryError && (
                <WarningAlert message={DRAG_AND_DROP_DIRECTORY_ERROR} />
              )}
              <FilesSelected>
                {selectedFiles.length} {`file${selectedFiles.length === 1 ? '' : 's'} selected`}
              </FilesSelected>
            </Fragment>
          )}

          <StyledList
            itemCount={selectedFiles.length}
            // The itemSize needs to be taller if displaying the Attachment details sub-form
            itemSize={() => (displayAttachmentOptions ? TALL_ITEM_SIZE : DEFAULT_ITEM_SIZE)}
            itemData={itemData}
            height={height}
            width={width}
            itemKey={itemKey}
            ref={listRef}
          >
            {SelectedFileItem as any}
          </StyledList>
        </Fragment>
      )}
    </Fragment>
  );
}

interface ErrorSummaryProps {
  validFilesCount: number;
}

function ErrorSummary({ validFilesCount }: ErrorSummaryProps) {
  const { selectedFiles } = useUploadContext();
  const errors = selectedFiles.filter(({ status }) => status === 'error');

  return (
    <Fragment>
      <p>{validFilesCount - errors.length} files uploaded successfully.</p>
      <ErrorWrapper>
        <p>The following files were not uploaded:</p>
        <ul>
          {errors.map(file => (
            <li key={file.sourceName}>{file.sourceName}</li>
          ))}
        </ul>
      </ErrorWrapper>
    </Fragment>
  );
}
